import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { BadgeVariant, Button, Flex, Input, Modal, StatusBadge, Text, TextVariant, Wallet } from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useFormik } from 'formik'
import { BNToNumberWithDecimals, blockInvalidFloat } from '@mprolab/sdk'
import isNil from 'lodash/isNil'
import UseAnimations from 'react-useanimations'
import loading from 'react-useanimations/lib/loading'
import Image from 'components/CompatImage'
import { tokenLogoSrc } from 'utils/tokenLogoUri'
import { MaxButton } from 'views/Exchange/components/General'
import { displayDuration } from 'utils/displayDuration'
import fetchTokenBalance from 'utils/fetchTokenBalance'
import { logMessage } from 'utils/logMessage'
import { formatNumberWithSpaces } from 'utils/formatNumberWithSpaces'
import { useBep20Contract } from 'hooks/useContract'
import { ethers } from 'ethers'
import { observer } from 'mobx-react-lite'
import { StakeProgress, stakeStore } from 'mobxStores/StakeStore'
import { getErrorCodesDescription } from 'utils/getErrorCodesDescription'
import ChainScanFooter from 'components/ChainScanFooter'
import { useClassicStake, useStakeTimestampCounter } from '../classicStake.logic'
import { StakeOverviewInfo } from '../components'

const ContentWrapper = styled(Flex)`
  flex-direction: column;
  margin-top: 32px;
  width: 550px;
  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100%;
  }
`

const Wrapper = styled(Flex)`
  gap: 8px;
  align-items: center;
`

const TokenWrapper = styled(Flex)`
  align-items: center;
  background: #171717;
  padding: 8px 16px 8px 8px;
  gap: 8px;
  border-radius: 45px;
`

const TableWrapper = styled(Flex)`
  padding: 16px;
  border-radius: 8px;
  justify-content: center;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.gray10};
`

const BadgeWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
`

interface JoinStakeProps {
  fetchClassicStakeConfig: (userAccount?: string, refetch?: boolean) => Promise<void>
  isLoading?: boolean
  onDismiss?: () => void
}

const JoinStakeModal: React.FC<JoinStakeProps> = observer(({ onDismiss, isLoading, fetchClassicStakeConfig }) => {
  const { stake, staker, stakeProgress, error, txHash } = stakeStore
  const { account, provider } = useWeb3React()
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { totalTime, counterLoading } = useStakeTimestampCounter(stake)
  const [tokenBalance, setTokenBalance] = useState<number>()
  const [loadingBalance, setLoadingBalance] = useState(true)
  const { stake: joinStake } = useClassicStake()
  const stakeTokenInterface = useBep20Contract(stake.stakeTokenAddress)

  const stakeToken = (): { name: string; image: ReactNode } => {
    const tokenAImage = stake?.token0?.logoURI || stake?.stakeToken?.logoURI
    const tokenBImage = stake?.token1?.logoURI

    return {
      name: stake.stakeToken?.token || `${stake.token0?.token}-${stake.token1?.token}`,
      image: (
        <Flex width={tokenBImage ? '32px' : '16'} height="16px" position="relative">
          <Image src={tokenLogoSrc(tokenAImage)} width={16} height={16} />
          {tokenBImage && <Image src={tokenLogoSrc(tokenBImage)} width={16} height={16} />}
        </Flex>
      )
    }
  }

  const { name: stakeTokenName, image: stakeTokenImage } = stakeToken()

  const dataLoading = isLoading || counterLoading

  const formik = useFormik({
    validateOnBlur: false,
    validateOnChange: true,
    initialValues: {
      amount: 0
    },
    validationSchema: Yup.object().shape({
      amount: Yup.number()
        .required(t('stakingPanel.amountRequired'))
        .test('max', t('stakingPanel.amountExceedsTokenBalance'), function maxAmountValidation(value) {
          return value <= tokenBalance
        })
        .test('min', t('stakingPanel.amountRequired'), function minAmountValidation(value) {
          return value > 0
        })
    }),
    onSubmit: async (values, { validateForm }) => {
      try {
        stakeStore.setStakeProgress(StakeProgress.PENDING)
        await validateForm(values)
        // parseUnits defaults to 18 decimals. The stake token decides — a
        // token with different decimals would approve and stake an amount off
        // by orders of magnitude, silently, because MPRO happens to be 18.
        const allowance = await stakeTokenInterface.allowance(account, stake.contractAddress)
        if (allowance.lt(ethers.utils.parseUnits(values.amount.toString(), stake.stakeToken?.decimals ?? 18))) {
          stakeStore.setStakeProgress(StakeProgress.APPROVING)
          const tx = await stakeTokenInterface.approve(stake.contractAddress, ethers.constants.MaxUint256.toString())
          await tx.wait()
        }
        stakeStore.setStakeProgress(StakeProgress.CONFIRMING)
        await joinStake(
          stake.contractAddress,
          ethers.utils.parseUnits(values.amount.toString(), stake.stakeToken?.decimals ?? 18)
        )
        await fetchClassicStakeConfig(account, true)
      } catch (error) {
        stakeStore.setStakeProgress(StakeProgress.ERROR)
      }
    }
  })

  const getTokenBalance = useCallback(async () => {
    try {
      const tokenBalance = await fetchTokenBalance({
        tokenAddress: stake.stakeTokenAddress,
        account,
        provider,
        chainId: stake.stakeToken?.chainId,
        decimals: stake.stakeToken?.decimals
      })

      setTokenBalance(BNToNumberWithDecimals(tokenBalance))
    } catch (error) {
      logMessage(`Token balance error:${error}`)
    } finally {
      setLoadingBalance(false)
    }
  }, [stake?.stakeToken, account, loadingBalance])

  useEffect(() => {
    getTokenBalance()
  }, [stake?.stakeToken, account, loadingBalance])

  const joinStakeModalContent = () => {
    switch (stakeProgress) {
      case StakeProgress.INITIAL:
        return (
          <ContentWrapper>
            <Flex fullWidth justifyContent="space-between" mb="8px">
              <Text variant={TextVariant.TEXT_XS_BOLD}>{t('stakingPanel.stakeAmount')}</Text>
              <Flex gap="8px" alignItems="center">
                <Wallet width={16} height={16} color={colors.gray3} />
                {loadingBalance ? (
                  <Flex minWidth="50px" justifyContent="center">
                    <UseAnimations animation={loading} size={14} strokeColor={colors.white3} />
                  </Flex>
                ) : (
                  <Text variant={TextVariant.TEXT_XS_BOLD} color={colors.gray3}>
                    {`${tokenBalance} ${stakeTokenName}`}
                  </Text>
                )}
              </Flex>
            </Flex>
            <Input
              value={formik.values.amount}
              name="amount"
              type="number"
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  e.preventDefault()
                }
                blockInvalidFloat(e)
              }}
              error={!isNil(formik.errors.amount)}
              errorMsg={formik.errors.amount as string}
              onChange={(e) => {
                let { value } = e.target
                value = value.replace(',', '.')
                formik.setFieldValue('amount', value)
              }}
              endIcon={
                <Wrapper>
                  {tokenBalance > 0 && <MaxButton onClick={() => formik.setFieldValue('amount', tokenBalance)} />}
                  <TokenWrapper>
                    {stakeTokenImage}
                    <Text variant={TextVariant.BODY_SMALL_BOLD}>{stakeTokenName}</Text>
                  </TokenWrapper>
                </Wrapper>
              }
            />
            <Flex mt="32px" mb="8px">
              <Text variant={TextVariant.BODY_SMALL} textTransform="uppercase" color={colors.gray6}>
                {t('stakingPanel.overview')}
              </Text>
            </Flex>
            <TableWrapper>
              <StakeOverviewInfo
                label={t(`stakingPanel.currentlyStakeAmount`)}
                value={`${formatNumberWithSpaces(
                  BNToNumberWithDecimals(staker?.staked, stake.stakeToken?.decimals, 6)
                )} ${stakeTokenName}`}
                {...{ isLoading: dataLoading }}
              />
              <StakeOverviewInfo
                label={t(`stakingPanel.totalStakeAmount`)}
                value={`${formatNumberWithSpaces(
                  BNToNumberWithDecimals(stake?.totalStakedSupply, stake.stakeToken?.decimals, 6)
                )} ${stakeTokenName}`}
                {...{ isLoading: dataLoading }}
              />
              <StakeOverviewInfo
                label={t(`stakingPanel.apy`)}
                value={`${stake?.apr}%`}
                valueColor={colors.green}
                {...{ isLoading: dataLoading }}
              />
              <StakeOverviewInfo
                label={t(`stakingPanel.stakeDuration`)}
                value={displayDuration({ time: totalTime })}
                isLast
                {...{ isLoading: dataLoading }}
              />
            </TableWrapper>
            <Flex mt="32px">
              <Button fullWidth disabled={!isNil(formik.errors.amount)} onClick={() => formik.handleSubmit()}>
                {t('stakingPanel.stake')}
              </Button>
            </Flex>
          </ContentWrapper>
        )
      case StakeProgress.APPROVING:
        return (
          <BadgeWrapper>
            <StatusBadge title={t('modal.transactionApproving')} variant={BadgeVariant.PENDING} />
          </BadgeWrapper>
        )
      case StakeProgress.CONFIRMING:
        return (
          <BadgeWrapper>
            <StatusBadge title={t('modal.continueInWallet')} variant={BadgeVariant.PENDING} />
          </BadgeWrapper>
        )
      case StakeProgress.PENDING:
        return (
          <BadgeWrapper>
            <StatusBadge title={t('transactionPending')} variant={BadgeVariant.PENDING} />
          </BadgeWrapper>
        )
      case StakeProgress.SUCCESS:
        return (
          <Flex flexDirection="column" gap="16px">
            <BadgeWrapper>
              <StatusBadge title={t('modal.transactionSucessful')} variant={BadgeVariant.SUCCESS} />
            </BadgeWrapper>
            <ChainScanFooter chainId={stake.stakeToken?.chainId} transactionHash={txHash} />
          </Flex>
        )
      case StakeProgress.ERROR:
      case StakeProgress.REJECTED:
        return (
          <BadgeWrapper>
            <StatusBadge title={getErrorCodesDescription(t, error)} variant={BadgeVariant.ERROR} />
          </BadgeWrapper>
        )

      default:
        return <></>
    }
  }

  const modalTitle = () => {
    switch (stakeProgress) {
      case StakeProgress.INITIAL:
      case StakeProgress.SUCCESS:
        return `${t('stakingPanel.stake')} ${stakeTokenName}`
      case StakeProgress.CONFIRMING:
        return t('modal.confirmTransaction')
      case StakeProgress.APPROVING:
        return t('modal.approving')
      case StakeProgress.PENDING:
        return t('modal.transactionConfirmation')
      case StakeProgress.ERROR:
        return t('modal.transactionFailed')
      case StakeProgress.REJECTED:
        return t('modal.rejected')
      default:
        return ''
    }
  }

  return (
    <Modal onDismiss={onDismiss} hideCloseButton={false} title={modalTitle()}>
      <ContentWrapper>{joinStakeModalContent()}</ContentWrapper>
    </Modal>
  )
})

export default JoinStakeModal
